import { create } from 'zustand';
import { getChecklistProtocol } from '../api/generated/checklist-protocol/checklist-protocol';
import { getDocuments } from '../api/generated/documents/documents';
import type {
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolDto,
  FacilityFlowCoreModulesPlanningDtosChecklistsOpenChecklistProtocolDto,
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolEmployeeDto,
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolTaskDto,
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolItemDto,
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolReportDto,
} from '../api/generated/models';

const { getApiV1ChecklistProtocol, getApiV1ChecklistProtocolOpen } = getChecklistProtocol();
const { getApiV1DocumentsIdContent } = getDocuments();

export type ChecklistProtocol = FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolDto;
export type OpenChecklistProtocol =
  FacilityFlowCoreModulesPlanningDtosChecklistsOpenChecklistProtocolDto;
export type ChecklistProtocolEmployee =
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolEmployeeDto;
export type ChecklistProtocolTask =
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolTaskDto;
export type ChecklistProtocolItem =
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolItemDto;
/** A report ("Meldung") submitted on the protocol's day — only the day protocol carries them. */
export type ChecklistProtocolReport =
  FacilityFlowCoreModulesPlanningDtosChecklistsChecklistProtocolReportDto;

export interface ProtocolFilters {
  date: string;
  employeeId?: string;
  objectId?: string;
}

/**
 * The open-items view has no date — it spans every past day. `includeCompleted` also lists
 * fully confirmed tasks; the server then limits the range (default: the last 14 days).
 */
export interface OpenProtocolFilters extends Omit<ProtocolFilters, 'date'> {
  includeCompleted?: boolean;
}

interface ChecklistProtocolState {
  protocol: ChecklistProtocol | null;
  loading: boolean;
  error: string | null;

  /** Past days that still carry unconfirmed points; null until loaded. */
  openProtocol: OpenChecklistProtocol | null;
  openLoading: boolean;
  openError: string | null;

  fetchProtocol: (filters: ProtocolFilters) => Promise<void>;
  fetchOpenProtocol: (filters: OpenProtocolFilters) => Promise<void>;
  openPhoto: (documentId: string) => Promise<void>;
  clear: () => void;
}

export const useChecklistProtocolStore = create<ChecklistProtocolState>((set) => ({
  protocol: null,
  loading: false,
  error: null,
  openProtocol: null,
  openLoading: false,
  openError: null,

  fetchProtocol: async ({ date, employeeId, objectId }) => {
    set({ loading: true, error: null });
    try {
      const data = await getApiV1ChecklistProtocol({
        date,
        employeeId: employeeId || undefined,
        objectId: objectId || undefined,
      });
      set({ protocol: data, loading: false });
    } catch {
      set({ error: 'checklistProtocol:errors.fetchFailed', loading: false, protocol: null });
    }
  },

  fetchOpenProtocol: async ({ employeeId, objectId, includeCompleted }) => {
    set({ openLoading: true, openError: null });
    try {
      const data = await getApiV1ChecklistProtocolOpen({
        employeeId: employeeId || undefined,
        objectId: objectId || undefined,
        includeCompleted: includeCompleted || undefined,
      });
      set({ openProtocol: data, openLoading: false });
    } catch {
      set({
        openError: 'checklistProtocol:open.errors.fetchFailed',
        openLoading: false,
        openProtocol: null,
      });
    }
  },

  // The blob endpoint needs the auth cookie and the X-Tenant-Id header the axios
  // instance injects, so a plain link would fail tenant validation.
  openPhoto: async (documentId) => {
    const blob = await getApiV1DocumentsIdContent(documentId);
    const url = URL.createObjectURL(blob as Blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  clear: () => set({ protocol: null, error: null, openProtocol: null, openError: null }),
}));
